import CustomLabel from '@/common/components/base/customLabel/CustomLabel';
import CircularLoading from '@/common/components/base/Loading/CircularLoading';
import Typography from '@/common/components/base/typography/Typography';
import CardButton from '@/common/components/cardButton/CardButton';
import MainLayout from '@/common/components/layouts/MainLayout';
import AdjusterIcon from '@/modules/adjusterReportModules/IncidentParticipants/assets/icons/adjuster-icon.svg?react';
import BankAccountIcon from '@/modules/adjusterReportModules/IncidentParticipants/assets/icons/bank-account.svg?react';
import OwnerIcon from '@/modules/adjusterReportModules/IncidentParticipants/assets/icons/owner.svg?react';
import PropertyInsuranceIcon from '@/modules/adjusterReportModules/IncidentParticipants/assets/icons/property-damaged.svg?react';
import RefusalReceiveDamagesIcon from '@/modules/adjusterReportModules/IncidentParticipants/assets/icons/refusalReceiveDamagesIcon.svg?react';
import VehicleInformationIcon from '@/modules/adjusterReportModules/IncidentParticipants/assets/icons/veichel-info.svg?react';
import { ADJUSTER_REPORT_BODY_DAMAGE_LINKS } from '@/modules/adjusterReportModules/adjusterReport/links/bodyDamage/bodyDamageLinks';
import { GetInsurerInformationTabsTabType } from '@/modules/adjusterReportModules/Insurer-information/queries/getInsurerInformationTabs/getInsurerInformationTabs.type';
import { getInsurerInformationTabsQueryOptions } from '@/modules/adjusterReportModules/Insurer-information/queries/getInsurerInformationTabs/getParticipantsTabsQueryOptions';
import getInsurerInformationTabsAlertsQueryOptions from '@/modules/adjusterReportModules/Insurer-information/queries/getInsurerInformationTabsAlerts/getInsurerInformationTabsAlertsQueryOptions';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { css } from 'styled-system/css';
import { Box, Flex } from 'styled-system/jsx';
import DocumentsIcon from '@/common/assets/icons/documents.svg?react';

export default () => {
  return (
    <MainLayout title="بیمه گذار" header="titleHeader">
      <InsurerInformation />
    </MainLayout>
  );
};

const InsurerInformation = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const { data } = useQuery(
    getInsurerInformationTabsQueryOptions({
      id: +id!,
    }),
  );
  const { data: alert, isPending } = useQuery(
    getInsurerInformationTabsAlertsQueryOptions({
      id: id!,
    }),
  );

  const refusalReceiveDamages = alert?.alert_detail?.find(
    (item) => item.type === 'refusal_receive_damages',
  );

  const renderRoute = (tabName: GetInsurerInformationTabsTabType['name']) => {
    const BODY_DAMAGE_INFORMATION =
      ADJUSTER_REPORT_BODY_DAMAGE_LINKS.BODY_DAMAGE_INFORMATION({ id: id! });
    const vehicleInformationObject = data?.find(
      (item) => item.name === 'vehicle_information',
    )?.editable
      ? BODY_DAMAGE_INFORMATION.VEHICLE_INFORMATION.partOne
      : BODY_DAMAGE_INFORMATION.VEHICLE_INFORMATION.show;
    const bankAccountObject = data?.find((item) => item.name === 'bank')
      ?.editable
      ? BODY_DAMAGE_INFORMATION.BANK_ACCOUNT.edit
      : BODY_DAMAGE_INFORMATION.BANK_ACCOUNT.show;
    const propertyInsuranceObject = data?.find(
      (item) => item.name === 'property_insurance',
    )?.editable
      ? BODY_DAMAGE_INFORMATION.PROPERTY_INSURANCE.edit
      : BODY_DAMAGE_INFORMATION.PROPERTY_INSURANCE.show;
    const ownerObject = data?.find((item) => item.name === 'owner')?.editable
      ? BODY_DAMAGE_INFORMATION.DRIVER_OWNER_INFORMATION.driver
      : BODY_DAMAGE_INFORMATION.DRIVER_OWNER_INFORMATION.show;
    const adjsutingObject = BODY_DAMAGE_INFORMATION.EXPERT.entryTabs;

    const imagesObject =
      ADJUSTER_REPORT_BODY_DAMAGE_LINKS.BODY_DAMAGE_IMAGES({
        id: id!,
      }).imagesTabPage;

    let tabIcon: Record<GetInsurerInformationTabsTabType['name'], string> = {
      owner: ownerObject,
      adjusting: adjsutingObject,
      bank: bankAccountObject,
      documents: imagesObject,
      property_insurance: propertyInsuranceObject,
      vehicle_information: vehicleInformationObject,
    };
    return tabIcon[tabName];
  };

  const goToFile = (tabName: GetInsurerInformationTabsTabType['name']) => {
    navigate(renderRoute(tabName));
  };

  return (
    <Box pt="2rem">
      {refusalReceiveDamages && (
        <Flex
          background={'purpleLinear'}
          borderRadius={8}
          px={16}
          py={4}
          alignItems="center"
          gap={16}
        >
          <Box>
            <RefusalReceiveDamagesIcon />
          </Box>
          <Box>
            <Typography textStyle={'b1'} color="purple-1" textAlign="center">
              زیان‌دیده از دریافت مبلغ خسارت انصراف داده است.
            </Typography>
          </Box>
        </Flex>
      )}
      {isPending ? (
        <Box display="flex" alignItems="center" justifyContent="center">
          <CircularLoading color="colors.purple-primary" />
        </Box>
      ) : (
        <>
          {data?.map((item) => (
            <CardButton
              key={item?.name}
              cardProps={{
                className: css({ marginY: '1rem' }),
                onClick: () => item?.is_card_active && goToFile(item?.name),
                cursor: item?.is_card_active ? 'pointer' : 'not-allowed',
              }}
              hasArrow={item?.is_card_active}
              renderTag={() =>
                item?.label &&
                item?.is_card_active && (
                  <CustomLabel
                    title={item?.label?.fa_const}
                    color={
                      item?.label?.const === 'completed' ? 'green' : 'yellow'
                    }
                  />
                )
              }
              renderIcon={() => renderIcon(item?.name)}
              title={item?.fa_name}
            />
          ))}
        </>
      )}
    </Box>
  );
};

const renderIcon = (tabName: GetInsurerInformationTabsTabType['name']) => {
  let tabIcon: Record<
    GetInsurerInformationTabsTabType['name'],
    React.JSX.Element
  > = {
    adjusting: <AdjusterIcon />,
    bank: <BankAccountIcon />,
    documents: <DocumentsIcon color="red" />,
    owner: <OwnerIcon />,
    property_insurance: <PropertyInsuranceIcon />,
    vehicle_information: <VehicleInformationIcon />,
  };
  return tabIcon[tabName];
};
